import React, {Fragment, useEffect, useRef, useState} from "react";
import {useTranslation} from "react-i18next";
import {Listbox} from "@headlessui/react";
import {CheckIcon} from "@heroicons/react/20/solid";
import clsx from "clsx";
import {FieldErrors} from "react-hook-form";
import {Overlay} from "react-overlays";
import FeatherIcon from "components/FeatherIcon";

export interface BirthdateInputData {
    id?: number;
    value: number;
    name: string;
}

interface BirthdateInputProps {
    className?: string;
    errors?: FieldErrors;
    data: BirthdateInputData[];
    onChange: (value: number) => void;
    disabled?: boolean;
    placeholder: string;
    reset?: boolean;
    callbackReset?: (value: boolean) => void;
    name: string;
    defaultValue?: string | number | null;
}

const BirthdateInput = (props: BirthdateInputProps) => {
    const {t} = useTranslation();
    const [selected, setSelected] = useState<BirthdateInputData | null>(null);
    const selectedOptionRef = useRef<HTMLLIElement>(null);
    const targetRef = useRef(null);
    const [show, setShow] = useState(false);
    const options = props.data;

    const onChangeHandler = (data: BirthdateInputData) => {
        setSelected(data);
        setShowHandler(false);
        props.onChange(data.value);
    };

    const setShowHandler = (value: boolean) => {
        setShow(value);
    };

    useEffect(() => {
        if (props.reset) {
            props.callbackReset && props.callbackReset(false);
            setSelected(null);
        }
    }, [props.reset]);

    useEffect(() => {
        if (props.defaultValue !== null) {
            const defaultValue = props.data.find(item => item.value === props.defaultValue);
            setSelected(defaultValue || null);
        }
    }, [props.defaultValue]);

    return (
        <div className={clsx("BirthdateInput flex-1 relative", props.className)}>
            <Listbox value={selected} onChange={onChangeHandler} disabled={props.disabled}>
                {({open}) => (
                    <div className="w-full">
                        <Listbox.Button
                            ref={targetRef}
                            className={clsx(
                                show ? "SHOWN" : "NOTSHOWN",
                                props.disabled ? "DISABLED" : "",
                                "z-10 h-full flex items-center justify-between w-full pl-3 py-2 rounded-[5px] border-arbo-borderGray border text-base md:text-base font-medium",
                                props.disabled
                                    ? selected
                                        ? "bg-gray-100 text-gray-600"
                                        : "bg-gray-100 text-gray-400"
                                    : selected
                                      ? "bg-white text-black"
                                      : "bg-white text-gray-400",
                                selected ? "text-black border-arbo-borderGray" : "text-gray-400",
                                props.errors?.[props.name] && !open && !selected && "border-red-500"
                            )}
                            onClick={() => setShowHandler(!show)}
                        >
                            <div className={"flex flex-row items-center"}>
                                <span className="block truncate">{selected ? selected.name : t(props.placeholder)}</span>
                            </div>
                            {/*<span className="pointer-events-none pl-0.5">*/}
                            <span className="pointer-events-none flex h-full pl-1 pr-1 items-center">
                                <FeatherIcon name={"chevron-down"} strokeWidth={2} className={"size-6 flex-none text-black"} />
                                {/*<ChevronDownIcon className={"w-4 h-4 stroke-2 text-black"} />*/}
                            </span>
                        </Listbox.Button>
                        <Overlay placement="bottom-start" show={show} target={targetRef.current} onHide={() => setShowHandler(false)} rootClose>
                            {({props, placement}) => (
                                <Listbox.Options
                                    {...props}
                                    style={{
                                        ...props.style,
                                        transition: "maxHeight 0.3s ease-in-out",
                                        // @ts-ignore
                                        width: targetRef?.current ? targetRef.current.getBoundingClientRect().width : "100%",
                                    }}
                                    className={clsx(
                                        "transition-all duration-300 ease-in h-full drop-shadow shadow-sm z-10 max-h-[15rem] w-full max-w-full rounded-md bg-white overflow-x-hidden py-1 text-base",
                                        placement
                                    )}
                                    unmount={false}
                                    onLoad={() => {
                                        if (selected && selectedOptionRef.current) {
                                            selectedOptionRef.current.scrollIntoView({
                                                behavior: "smooth",
                                                block: "nearest",
                                            });
                                        }
                                    }}
                                >
                                    {options.map(person => (
                                        /* Use the `active` state to conditionally style the active option. */
                                        /* Use the `selected` state to conditionally style the selected option. */
                                        <Listbox.Option key={person.value} value={person} as={Fragment}>
                                            {({active, selected}) => {
                                                return (
                                                    <li
                                                        ref={selected ? selectedOptionRef : null}
                                                        className={clsx(
                                                            "flex flex-row cursor-pointer select-none py-1 pr-0 text-sm w-full",
                                                            active || selected ? "bg-gray-200/80 text-black" : ""
                                                        )}
                                                    >
                                                        {selected && (
                                                            <span className={"w-5 flex items-center"}>
                                                                <CheckIcon className={"w-3.5 h-3.5 ml-0.5"} />
                                                            </span>
                                                        )}
                                                        <span className={clsx("block truncate", !selected && "pl-5")}>{person.name}</span>
                                                    </li>
                                                );
                                            }}
                                        </Listbox.Option>
                                    ))}
                                </Listbox.Options>
                            )}
                        </Overlay>
                    </div>
                )}
            </Listbox>
        </div>
    );
};

export default BirthdateInput;
